import { CACHE_TTL_DURATIONS, SessionStorageKeys } from '../../utilities/storageUtils';
import GWRApiClient from './GWRApiClient';

// ─── Internal cache implementation ───────────────────────────────────────────

const SNAPSHOT = {
  storageKey: SessionStorageKeys.CACHE_SNAPSHOT_KEY,
  ttlSeconds: 10
};

/** Basic in-memory cache with TTL support */
class BasicCache {
  constructor() {
    this._store = new Map();
    this._sweepScheduled = false;
  }

  static getInstance() {
    if (!BasicCache._instance) BasicCache._instance = new BasicCache();
    return BasicCache._instance;
  }

  get(key) {
    this._scheduleSweep();
    const entry = this._store.get(key);
    if (!entry) return null;
    if (Date.now() > entry.expiry) return null;
    return entry.data;
  }

  set(key, data, ttlMs) {
    this._scheduleSweep();
    const expiry = Date.now() + ttlMs;
    this._store.set(key, { data, expiry });
  }

  delete(key) {
    this._store.delete(key);
  }

  // Review the use of this method for deletion, this will not be removed for now
  has(key) {
    const entry = this._store.get(key);
    if (!entry) return false;
    return Date.now() <= entry.expiry;
  }

  clear() {
    this._store.clear();
  }

  _scheduleSweep() {
    if (this._sweepScheduled) return;
    this._sweepScheduled = true;
    setTimeout(() => {
      this._sweepScheduled = false;
      this._clearStaleEntries();
    });
  }

  _clearStaleEntries() {
    const now = Date.now();
    for (const [key, entry] of this._store.entries()) {
      if (entry.expiry < now) this._store.delete(key);
    }
  }
}

BasicCache._instance = null;

/** Persisted cache that extends BasicCache with save/rehydrate functionality */
class PersistedCache extends BasicCache {
  constructor() {
    super();
    this._rehydrateFromSnapshot();
    // 'pagehide' fires reliably on mobile/bfcache where 'beforeunload' often does not,
    // and avoids disqualifying the page from the back/forward cache.
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', () => this._saveSnapshot());
    }
  }

  static getInstance() {
    if (!PersistedCache._instance) PersistedCache._instance = new PersistedCache();
    return PersistedCache._instance;
  }

  _rehydrateFromSnapshot() {
    try {
      const raw = sessionStorage.getItem(SNAPSHOT.storageKey);
      if (!raw) return;
      const { savedAt, entries } = JSON.parse(raw);
      if (Date.now() - savedAt > SNAPSHOT.ttlSeconds * 1000) return;
      const now = Date.now();
      for (const [key, entry] of Object.entries(entries)) {
        if (entry.expiry > now) this._store.set(key, entry);
      }
    } catch {
      // ignore malformed snapshot
    } finally {
      sessionStorage.removeItem(SNAPSHOT.storageKey);
    }
  }

  _saveSnapshot() {
    if (this._store.size === 0) return;
    try {
      const entries = Object.fromEntries(this._store);
      sessionStorage.setItem(SNAPSHOT.storageKey, JSON.stringify({ savedAt: Date.now(), entries }));
    } catch {
      // ignore quota or serialization errors
    }
  }
}

PersistedCache._instance = null;

// ─── Public base class ────────────────────────────────────────────────────────

/**
 * Extend instead of GWRApiClient to add in-memory GET caching.
 * Mutations (PUT / POST / DELETE / PATCH) are inherited unchanged.
 *
 *   class MyApiClient extends GWRCachableApiClient {
 *     constructor(url, token) {
 *       super(url, token, { cacheTTL: 3 * 60 * 1000 });
 *     }
 *   }
 *
 * @abstract
 * @extends GWRApiClient
 */
export default class GWRCachableApiClient extends GWRApiClient {
  /**
   * @param {string} url
   * @param {string} token
   * @param {{ cacheTTL?: number }} options
   *   cacheTTL: TTL in ms for cached responses (default 5 min).
   */
  constructor(url = '', token = '', options = {}) {
    super(url, token);
    this._cacheTTL = options.cacheTTL ?? CACHE_TTL_DURATIONS.DEFAULT;
    this._store = PersistedCache.getInstance();
    this._pending = BasicCache.getInstance();
  }

  _buildCacheKey(url, params = {}) {
    return btoa(encodeURIComponent(url + JSON.stringify(params)));
  }

  async _getWithCache(urlRequest, params, fetchFn) {
    const key = this._buildCacheKey(urlRequest, params);

    // get() returns null for missing OR expired entries; since we only cache
    // responses with defined data, a non-null value is always a valid hit.
    const cached = this._store.get(key);
    if (cached !== null) {
      return cached;
    }

    const inflight = this._pending.get(key);
    if (inflight !== null) {
      return inflight;
    }

    const pending = fetchFn()
      .then(response => {
        // (loose != catches both null and undefined) and that is the values we want to check
        if (response?.data != null) {
          this._store.set(key, response, this._cacheTTL);
        }
        return response;
      })
      .finally(() => {
        this._pending.delete(key);
      });

    this._pending.set(key, pending, this._cacheTTL);
    return pending;
  }

  /** @override */
  async getApiCallAuth(urlRequest, params = {}, requiredFields = [], isGwlEndpoint = true, addApiLog = false) {
    return this._getWithCache(urlRequest, params, () =>
      super.getApiCallAuth(urlRequest, params, requiredFields, isGwlEndpoint, addApiLog)
    );
  }

  /**
   * Clear all cache entries
   * and remove the snapshot from sessionStorage
   */
  clearAll() {
    this._store.clear();
    this._pending.clear();
    sessionStorage.removeItem(SNAPSHOT.storageKey);
  }
}
